import { AlertTriangle, CloudCheck, Globe, ShieldCheck, ShieldEllipsis } from 'lucide-react';
import { Input, RadioCards, SettingsSaveBar, Switch, TextArea } from '@airclou/ui';
import { useTranslation } from 'react-i18next';

const IDENTITY_PROVIDERS = [
  { id: 'Azure AD', label: 'Azure AD', icon: CloudCheck },
  { id: 'Google', label: 'Google', icon: Globe },
  { id: 'Okta', label: 'Okta', icon: ShieldCheck },
  { id: 'Other', label: 'Other', icon: ShieldEllipsis },
];

export type IdentityProvider = 'Azure AD' | 'Google' | 'Okta' | 'Other';

/**
 * Does this paste look like a SAML metadata document rather than a bare
 * certificate?
 *
 * Only a hint, and deliberately so: the authoritative parse happens in the auth
 * service, which is where the extraction logic already lives. This decides two
 * presentation questions — whether the sign-in URL is required of the admin, and
 * whether to say it will be read out of the document.
 */
export function looksLikeMetadataXml(text: string): boolean {
  return /<[a-z0-9:]*EntityDescriptor/i.test(text);
}

export interface SsoFormValues {
  ssoEnabled: boolean;
  identityProvider: IdentityProvider;
  idpSsoUrl: string;
  idpMetadataUrl: string;
  idpMetadata: string;
  enforceSSO: boolean;
}

export function GeneralTab(props: {
  values: SsoFormValues;
  setField: <K extends keyof SsoFormValues>(field: K, value: SsoFormValues[K]) => void;
  acsUrl: string;
  saving: boolean;
  isDirty: boolean;
  /** Why Save is blocked, or null when the form is ready to commit. */
  validationMessage: string | null;
  onSave: () => void;
  onDiscard: () => void;
}) {
  const { values, setField, acsUrl, saving, isDirty, validationMessage, onSave, onDiscard } = props;

  const { t } = useTranslation('dashboard');

  const metadataLooksLikeXml = looksLikeMetadataXml(values.idpMetadata);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div className="flex items-center gap-3">
          <div>
            <h4 className="font-medium">{t('sso.enableSSO')}</h4>
            <p className="text-sm text-muted-foreground">
              {t('sso.enableSSODescription')}
            </p>
          </div>
        </div>
        <Switch
          id="sso-enabled-toggle"
          aria-label={t('sso.enableSSO')}
          checked={values.ssoEnabled}
          onChange={(e) => setField('ssoEnabled', e.target.checked)}
          disabled={saving}
        />
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t('sso.identityProvider')}
          </label>
          <RadioCards
            name="identityProvider"
            value={values.identityProvider}
            onChange={(value: string) => setField('identityProvider', value as IdentityProvider)}
            options={IDENTITY_PROVIDERS}
            disabled={saving}
          />
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('sso.serviceProviderEntityId')}
        </label>
        <Input type="text" value="https://auth.airclou.com" onChange={() => {}} readOnly />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('sso.entityIdHint')}
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('sso.acsUrl')}
        </label>
        <Input type="url" value={acsUrl} onChange={() => {}} readOnly />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('sso.acsUrlHint')}
        </p>
      </div>

      {values.identityProvider === 'Azure AD' ? (
        <>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('sso.idpMetadataUrl')} <span className="text-red-500">*</span>
            </label>
            <Input
              type="url"
              value={values.idpMetadataUrl}
              onChange={(value) => setField('idpMetadataUrl', value)}
              placeholder="https://login.microsoftonline.com/your-tenant-id/federationmetadata/2007-06/federationmetadata.xml"
              disabled={saving}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('sso.idpMetadataUrlHint')}
            </p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('sso.ssoLoginUrl')}
            </label>
            <Input
              type="url"
              value={values.idpSsoUrl}
              onChange={(value) => setField('idpSsoUrl', value)}
              placeholder="https://login.microsoftonline.com/your-tenant-id/saml2"
              disabled={saving}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('sso.ssoLoginUrlHint')}
            </p>
          </div>
        </>
      ) : (
        <>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('sso.idpMetadata')} <span className="text-red-500">*</span>
            </label>
            <TextArea
              value={values.idpMetadata}
              onChange={(value) => setField('idpMetadata', value)}
              placeholder="Paste your identity provider metadata XML here..."
              disabled={saving}
              minRows={4}
              maxRows={8}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('sso.idpMetadataHint')}
            </p>
          </div>

          {/* Okta, Google and the rest hand out a metadata file rather than a
              URL, and the sign-in address lives inside it. Paste the document
              and it is read out for you; paste only a certificate and you have
              to supply the address, because SSO cannot work without one. */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('sso.ssoLoginUrlLabel')}
              {!metadataLooksLikeXml && <span className="text-red-500"> *</span>}
            </label>
            <Input
              type="url"
              value={values.idpSsoUrl}
              onChange={(value) => setField('idpSsoUrl', value)}
              placeholder="https://your-org.okta.com/app/.../sso/saml"
              disabled={saving}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {metadataLooksLikeXml ? t('sso.ssoLoginUrlFromMetadataHint') : t('sso.ssoLoginUrlNeededHint')}
            </p>
          </div>
        </>
      )}

      <div className="flex items-center justify-between p-4 border rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
          <div>
            <h4 className="font-medium text-yellow-800 dark:text-yellow-200">{t('sso.enforceSSO')}</h4>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              {t('sso.enforceSSODescription')}
            </p>
          </div>
        </div>
        <Switch
          id="sso-enforce-toggle"
          aria-label={t('sso.enforceSSO')}
          checked={values.enforceSSO && values.ssoEnabled}
          onChange={(e) => setField('enforceSSO', e.target.checked)}
          disabled={saving || !values.ssoEnabled}
        />
      </div>

      {values.enforceSSO && values.ssoEnabled && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-red-800 dark:text-red-200 mb-1">{t('sso.enforceWarningTitle')}</p>
              <p className="text-red-700 dark:text-red-300">
                {t('sso.enforceWarningDescription')}
              </p>
            </div>
          </div>
        </div>
      )}

      <SettingsSaveBar
        visible={isDirty}
        onSave={onSave}
        onDiscard={onDiscard}
        isSaving={saving}
        saveDisabled={Boolean(validationMessage)}
        message={validationMessage ?? t('common.unsavedChanges')}
        saveLabel={t('sso.saveConfiguration')}
        savingLabel={t('common.saving')}
        discardLabel={t('common.discard')}
      />
    </div>
  );
}
